import { isCsvFile } from '@/utils/csvAttachment';

const TEXT_PREFIX = 'ux3_conversation_attachment_text:';
const META_PREFIX = 'ux3_conversation_attachment_meta:';
const LEGACY_TEXT_PREFIX = 'ux3_bulk_csv_text:';
const LEGACY_META_PREFIX = 'ux3_bulk_csv_meta:';

export type ConversationAttachmentKind = 'csv';

export interface ConversationAttachmentMeta {
  kind: ConversationAttachmentKind;
  fileName: string;
  mimeType: string;
}

function textStorageKey(conversationId: string) {
  return `${TEXT_PREFIX}${conversationId}`;
}

function metaStorageKey(conversationId: string) {
  return `${META_PREFIX}${conversationId}`;
}

function readLegacyMeta(conversationId: string): ConversationAttachmentMeta | null {
  try {
    const raw = sessionStorage.getItem(`${LEGACY_META_PREFIX}${conversationId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { fileName: string };
    return {
      kind: 'csv',
      fileName: parsed.fileName,
      mimeType: 'text/csv',
    };
  } catch {
    return null;
  }
}

function readLegacyText(conversationId: string): string | null {
  return sessionStorage.getItem(`${LEGACY_TEXT_PREFIX}${conversationId}`);
}

/** Persist a file attachment for a conversation (session-scoped until consumed). */
export async function storeConversationAttachment(
  conversationId: string,
  file: File | null | undefined
): Promise<void> {
  const textKey = textStorageKey(conversationId);
  const metaKey = metaStorageKey(conversationId);

  if (!file) {
    sessionStorage.removeItem(textKey);
    sessionStorage.removeItem(metaKey);
    sessionStorage.removeItem(`${LEGACY_TEXT_PREFIX}${conversationId}`);
    sessionStorage.removeItem(`${LEGACY_META_PREFIX}${conversationId}`);
    return;
  }

  if (!isCsvFile(file)) {
    throw new Error('Only .csv files are supported.');
  }

  const text = await file.text();
  const meta: ConversationAttachmentMeta = {
    kind: 'csv',
    fileName: file.name,
    mimeType: file.type || 'text/csv',
  };

  sessionStorage.setItem(textKey, text);
  sessionStorage.setItem(metaKey, JSON.stringify(meta));
}

export function peekConversationAttachmentMeta(
  conversationId: string
): ConversationAttachmentMeta | null {
  try {
    const raw = sessionStorage.getItem(metaStorageKey(conversationId));
    if (raw) return JSON.parse(raw) as ConversationAttachmentMeta;
  } catch {
    /* fall through */
  }
  return readLegacyMeta(conversationId);
}

export function peekConversationAttachmentText(conversationId: string): string | null {
  return (
    sessionStorage.getItem(textStorageKey(conversationId)) ?? readLegacyText(conversationId)
  );
}

/** Overwrite stored CSV text after in-table edits (keeps attachment meta unchanged). */
export function setConversationAttachmentText(conversationId: string, text: string): void {
  sessionStorage.setItem(textStorageKey(conversationId), text);
  sessionStorage.setItem(`${LEGACY_TEXT_PREFIX}${conversationId}`, text);
}

export function consumeConversationAttachmentMeta(
  conversationId: string
): ConversationAttachmentMeta | null {
  const meta = peekConversationAttachmentMeta(conversationId);
  if (meta) {
    sessionStorage.removeItem(metaStorageKey(conversationId));
    sessionStorage.removeItem(`${LEGACY_META_PREFIX}${conversationId}`);
  }
  return meta;
}

export function consumeConversationAttachmentText(conversationId: string): string | null {
  const text = peekConversationAttachmentText(conversationId);
  if (text !== null) {
    sessionStorage.removeItem(textStorageKey(conversationId));
    sessionStorage.removeItem(`${LEGACY_TEXT_PREFIX}${conversationId}`);
  }
  return text;
}

export function hasConversationAttachment(conversationId: string): boolean {
  return Boolean(
    peekConversationAttachmentMeta(conversationId) && peekConversationAttachmentText(conversationId)
  );
}

/** Display metadata for workflow bubbles and chat UI. */
export function toMessageAttachment(
  meta: ConversationAttachmentMeta
): { kind: 'csv'; fileName: string } {
  return { kind: 'csv', fileName: meta.fileName };
}
